// /device?code=… — the verification page a device sends the user to during the
// RFC 8628 device-authorization flow: the Neomaris iOS app, or the Apple TV app.
// Auth-gated via the AppShell's RequireAuth: only a signed-in user can approve,
// and the approval is bound to that user (POST /v1/device/approve → the device's
// next token poll succeeds). The code may be prefilled from ?code= (the device's
// verification_uri_complete), and survives sign-in (password and Google both
// return to the full /device?code=… path), but the user must still explicitly
// approve. The finish names the device and sends the user back to it: the app
// polls and finishes signing in by itself (task device_page_finish).

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Status = 'awaiting_confirm' | 'submitting' | 'approved' | 'denied' | 'error';

interface ApiError {
  status?: number;
  body?: { error?: string; message?: string };
  message?: string;
}

interface ApproveResponse {
  ok?: boolean;
  action?: 'approved' | 'denied';
  /** The name the device gave itself when it asked to pair, e.g. "Mike's iPhone". */
  label?: string | null;
}

const ERROR_BANNERS: Record<string, string> = {
  not_found:
    'That code isn’t valid anymore. Codes expire after 15 minutes — start pairing again on your device to get a fresh one.',
  invalid_input:
    'That code doesn’t look right. Check that you typed it exactly as shown on your device.',
};

export function DeviceApprove() {
  useDocumentTitle('Connect a device - Neomaris');
  const [searchParams] = useSearchParams();
  const prefill = (searchParams.get('code') ?? searchParams.get('user_code') ?? '')
    .trim()
    .toUpperCase();

  const [status, setStatus] = useState<Status>('awaiting_confirm');
  const [code, setCode] = useState(prefill);
  const [error, setError] = useState<string | null>(null);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);

  useEffect(() => {
    if (prefill.length > 0) setCode(prefill);
  }, [prefill]);

  async function decide(action: 'approve' | 'deny') {
    const userCode = code.trim().toUpperCase();
    if (userCode.length === 0) {
      setStatus('error');
      setError('Enter the code shown on your device.');
      return;
    }
    setStatus('submitting');
    setError(null);
    try {
      const res = await api<ApproveResponse>('/v1/device/approve', {
        method: 'POST',
        body: { user_code: userCode, action },
      });
      const label = typeof res?.label === 'string' ? res.label.trim() : '';
      setDeviceLabel(label.length > 0 ? label : null);
      setStatus(action === 'deny' ? 'denied' : 'approved');
    } catch (err) {
      const e = err as ApiError;
      const banner =
        (typeof e.body?.error === 'string' ? ERROR_BANNERS[e.body.error] : undefined) ??
        e.body?.message ??
        'Something went wrong. Try again in a moment.';
      setStatus('error');
      setError(banner);
    }
  }

  function onApprove(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void decide('approve');
  }

  if (status === 'approved') {
    return (
      <section className="auth-panel">
        <div className="auth-card" data-testid="device-approved">
          <h2>Device connected</h2>
          <p className="auth-sub" data-testid="device-approved-message">
            {deviceLabel !== null ? (
              <>
                You’re signed in on <strong>{deviceLabel}</strong>.
              </>
            ) : (
              <>Your device is signed in.</>
            )}{' '}
            Go back to the Neomaris app; it will finish by itself.
          </p>
          <Link to="/devices" className="btn auth-submit">
            Manage linked devices
          </Link>
        </div>
      </section>
    );
  }

  if (status === 'denied') {
    return (
      <section className="auth-panel">
        <div className="auth-card" data-testid="device-denied">
          <h2>Pairing denied</h2>
          <p className="auth-sub">
            No problem — {deviceLabel ?? 'that device'} was not connected. If you didn’t start this,
            you can safely ignore it.
          </p>
          <Link to="/dashboard" className="btn btn-primary auth-submit">
            Back to dashboard
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-panel">
      <div className="auth-card">
        <h2>Connect a device</h2>
        <p className="auth-sub">
          {prefill.length > 0
            ? 'Check that this code matches the one on your device, then approve to sign it in to your Neomaris account.'
            : 'Enter the code shown on your device to sign it in to your Neomaris account.'}
        </p>
        {error !== null && (
          <ul className="form-errors" data-testid="error-banner">
            <li>{error}</li>
          </ul>
        )}
        <form onSubmit={onApprove} data-testid="device-approve-form">
          <div className="form-group">
            <label htmlFor="user-code">Pairing code</label>
            <input
              type="text"
              id="user-code"
              className="form-input auth-code-input"
              placeholder="ABCD-1234"
              value={code}
              onChange={(ev) => setCode(ev.target.value.toUpperCase())}
              autoComplete="off"
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Connecting…' : 'Approve'}
          </button>
          <button
            type="button"
            className="btn auth-submit auth-secondary"
            onClick={() => void decide('deny')}
            disabled={status === 'submitting'}
          >
            Deny
          </button>
        </form>
      </div>
    </section>
  );
}
